<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import api from "../../api/axios";

const router = useRouter();

const vista = ref("semana"); // dia | semana | mes
const fechaActual = ref(new Date());
const activities = ref([]);
const tasks = ref([]);
const cargando = ref(true);

const NOMBRE_TIPO = {
  llamada: "Llamada", correo: "Correo", whatsapp: "WhatsApp", reunion: "Reunión",
  videollamada: "Videollamada", visita: "Visita", seguimiento: "Seguimiento"
};

const DIAS_SEMANA_LABELS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function inicioDelDia(fecha) {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  return d;
}

function finDelDia(fecha) {
  const d = new Date(fecha);
  d.setHours(23, 59, 59, 999);
  return d;
}

function inicioSemana(fecha) {
  const d = inicioDelDia(fecha);
  const dia = d.getDay() === 0 ? 6 : d.getDay() - 1; // semana empieza lunes
  d.setDate(d.getDate() - dia);
  return d;
}

const rango = computed(() => {
  if (vista.value === "dia") {
    return { from: inicioDelDia(fechaActual.value), to: finDelDia(fechaActual.value) };
  }
  if (vista.value === "semana") {
    const inicio = inicioSemana(fechaActual.value);
    const fin = new Date(inicio);
    fin.setDate(fin.getDate() + 6);
    return { from: inicio, to: finDelDia(fin) };
  }
  // mes
  const inicio = new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth(), 1);
  const fin = new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth() + 1, 0);
  return { from: inicioDelDia(inicio), to: finDelDia(fin) };
});

const diasSemana = computed(() => {
  const inicio = inicioSemana(fechaActual.value);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(inicio);
    d.setDate(d.getDate() + i);
    return d;
  });
});

const diasMes = computed(() => {
  const inicioMes = new Date(fechaActual.value.getFullYear(), fechaActual.value.getMonth(), 1);
  const inicioGrilla = inicioSemana(inicioMes);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(inicioGrilla);
    d.setDate(d.getDate() + i);
    return d;
  });
});

// Encabezado de la actividad para verse de un vistazo en el calendario, sin
// abrir el evento: prioriza lo que el asesor escribió en "notas" (es lo que
// realmente dice de qué se trata), y si no hay notas cae al contacto o
// negocio relacionado, y por último al tipo solo.
function tituloActividad(a) {
  const tipo = NOMBRE_TIPO[a.type] || a.type;
  const contacto = a.contact_name
    ? `${a.contact_name}${a.contact_last_name ? " " + a.contact_last_name : ""}`
    : null;
  const detalle = (a.notes || "").trim() || contacto || a.deal_title;

  return detalle ? `${tipo}: ${detalle}` : tipo;
}

function itemsDelDia(fecha) {
  const ini = inicioDelDia(fecha).getTime();
  const fin = finDelDia(fecha).getTime();

  const acts = activities.value
    .filter(a => {
      const t = new Date(a.starts_at).getTime();
      return t >= ini && t <= fin;
    })
    .map(a => ({ kind: "activity", time: a.starts_at, label: tituloActividad(a), raw: a }));

  const tks = tasks.value
    .filter(t => {
      if (!t.due_at) return false;
      const time = new Date(t.due_at).getTime();
      return time >= ini && time <= fin;
    })
    .map(t => ({ kind: "task", time: t.due_at, label: t.title, raw: t }));

  return [...acts, ...tks].sort((a, b) => new Date(a.time) - new Date(b.time));
}

function formatHora(fecha) {
  return new Date(fecha).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

function formatDiaCorto(fecha) {
  return fecha.toLocaleDateString("es-CO", { weekday: "short", day: "numeric" });
}

function esHoy(fecha) {
  const hoy = new Date();
  return fecha.toDateString() === hoy.toDateString();
}

function esMesActual(fecha) {
  return fecha.getMonth() === fechaActual.value.getMonth();
}

function irADia(fecha) {
  fechaActual.value = fecha;
  vista.value = "dia";
}

function anterior() {
  const d = new Date(fechaActual.value);
  if (vista.value === "dia") d.setDate(d.getDate() - 1);
  else if (vista.value === "semana") d.setDate(d.getDate() - 7);
  else d.setMonth(d.getMonth() - 1);
  fechaActual.value = d;
}

function siguiente() {
  const d = new Date(fechaActual.value);
  if (vista.value === "dia") d.setDate(d.getDate() + 1);
  else if (vista.value === "semana") d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  fechaActual.value = d;
}

function hoy() {
  fechaActual.value = new Date();
}

function abrirItem(item) {
  const contactId = item.raw.contact_id;
  if (contactId) router.push(`/crm/contacts/${contactId}`);
}

async function cargar() {
  cargando.value = true;
  try {
    const res = await api.get("/crm/agenda", {
      params: { from: rango.value.from.toISOString(), to: rango.value.to.toISOString() }
    });
    activities.value = res.data.activities;
    tasks.value = res.data.tasks;
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la agenda",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  } finally {
    cargando.value = false;
  }
}

watch([vista, fechaActual], cargar);
onMounted(cargar);
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <h4 class="mb-0 agenda-header-title"><i class="bi bi-calendar3 me-2"></i>Agenda</h4>

      <div class="d-flex align-items-center gap-2">
        <div class="btn-group btn-group-sm">
          <button class="btn btn-outline-secondary agenda-view-btn" :class="{ active: vista === 'dia' }" @click="vista = 'dia'">Día</button>
          <button class="btn btn-outline-secondary agenda-view-btn" :class="{ active: vista === 'semana' }" @click="vista = 'semana'">Semana</button>
          <button class="btn btn-outline-secondary agenda-view-btn" :class="{ active: vista === 'mes' }" @click="vista = 'mes'">Mes</button>
        </div>

        <button class="btn btn-sm btn-light" @click="anterior"><i class="bi bi-chevron-left"></i></button>
        <button class="btn btn-sm btn-outline-primary" style="color: white;" @click="hoy">Hoy</button>
        <button class="btn btn-sm btn-light" @click="siguiente"><i class="bi bi-chevron-right"></i></button>
      </div>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <template v-else>
      <!-- VISTA DÍA -->
      <div v-if="vista === 'dia'" class="card">
        <div class="card-header">{{ fechaActual.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) }}</div>
        <div class="card-body">
          <div v-if="itemsDelDia(fechaActual).length === 0" class="text-muted small">Sin eventos este día.</div>
          <ul class="list-group" v-else>
            <li
              v-for="(item, idx) in itemsDelDia(fechaActual)"
              :key="idx"
              class="list-group-item d-flex gap-2"
              style="cursor: pointer"
              @click="abrirItem(item)"
            >
              <span class="badge" :class="item.kind === 'task' ? 'bg-info text-dark' : 'bg-primary'">{{ formatHora(item.time) }}</span>
              <span>
                <i class="bi" :class="item.kind === 'task' ? 'bi-check2-square' : 'bi-calendar-event'"></i>
                {{ item.label }}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <!-- VISTA SEMANA: en móvil cada día se apila a ancho completo (más
           legible que 7 columnas microscópicas); en md+ queda lado a lado. -->
      <div v-else-if="vista === 'semana'" class="row g-2 semana-row">
        <div v-for="dia in diasSemana" :key="dia.toISOString()" class="col-12 col-md">
          <div class="card h-100">
            <div class="card-header text-center py-1 small" :class="{ 'bg-primary text-white': esHoy(dia) }">
              {{ formatDiaCorto(dia) }}
            </div>
            <div class="card-body p-1 semana-dia-body">
              <div
                v-for="(item, idx) in itemsDelDia(dia)"
                :key="idx"
                class="small p-1 mb-1 rounded evento-semana"
                :class="item.kind === 'task' ? 'bg-info-subtle' : 'bg-primary-subtle'"
                style="cursor: pointer"
                :title="item.label"
                @click="abrirItem(item)"
              >
                {{ formatHora(item.time) }} {{ item.label }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- VISTA MES -->
      <div v-else class="card">
        <div class="card-header text-center">
          {{ fechaActual.toLocaleDateString("es-CO", { month: "long", year: "numeric" }) }}
        </div>
        <div class="card-body p-2 mes-card-body">
          <div class="mes-weekday-row">
            <div v-for="d in DIAS_SEMANA_LABELS" :key="d">{{ d }}</div>
          </div>
          <div class="mes-grid">
            <div
              v-for="dia in diasMes"
              :key="dia.toISOString()"
              class="border rounded mes-dia-celda"
              style="cursor: pointer"
              :class="{ 'bg-light': !esMesActual(dia), 'border-primary': esHoy(dia) }"
              @click="irADia(dia)"
            >
              <div class="small" :class="{ 'text-muted': !esMesActual(dia) }">{{ dia.getDate() }}</div>
              <span v-if="itemsDelDia(dia).length > 0" class="badge bg-primary">{{ itemsDelDia(dia).length }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<style scoped>
.semana-dia-body {
  min-height: 200px;
  flex: 1 1 auto;
  overflow-y: auto;
}

.mes-card-body {
  display: flex;
  flex-direction: column;
}

.mes-weekday-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #8898aa;
  margin-bottom: 0.35rem;
}

.mes-grid {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  gap: 0.25rem;
}

.mes-dia-celda {
  min-height: 80px;
  padding: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.evento-semana {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .semana-row {
    min-height: calc(100vh - 300px);
  }

  .mes-card-body {
    min-height: calc(100vh - 300px);
  }
}

@media (max-width: 767.98px) {
  .semana-dia-body {
    min-height: 100px;
  }

  .mes-dia-celda {
    min-height: 48px;
    font-size: 0.75rem;
  }
}

.agenda-header-title,
.agenda-view-btn {
  color: #fff !important;
  border-color: #fff !important;
}
</style>
